import React, { useState, useEffect } from 'react';
import { CarouselData } from './CarouselData'
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import './carousel.css';
import { inject, observer } from "mobx-react";
import { API_URL } from "../../assets/globalConstants";
import _useTranslate from '../../hooks/_useTranslate';

// Wolt-style hero: a contained carousel of rounded, bordered image cards — two per
// view on desktop (one on mobile), each carrying its own caption + CTA, with a gap
// between them. Reads the same nexio settings carousel model as before
// (webImages[0..2] + carouselHeader/Text/Link{n} via CarouselData), so admin edits
// still flow through; only the presentation changed.
const Carousel = ({ feathersStore }) => {

  const [index, setIndex] = useState(0);
  const [perView, setPerView] = useState(typeof window !== "undefined" && window.innerWidth <= 768 ? 1 : 2);
  const [paused, setPaused] = useState(false);

  const common = _useTranslate(feathersStore.language, "common");

  const settings = feathersStore.settings;
  const lang = feathersStore.language;
  const master = settings?.masterLanguage;

  // Translate a carousel field (master language → plain field, else …Translations/…English).
  const pick = (base) => {
    if (!settings || !base) return "";
    if (lang === master) return settings[base] || "";
    const tr = settings[base + "Translations"];
    const hit = Array.isArray(tr) ? tr.find(t => t?.code === lang)?.text : "";
    return hit || settings[base + "English"] || settings[base] || "";
  };

  const slides = CarouselData.map((meta, i) => {
    const img = settings?.webImages?.[i];
    if (!img) return null;
    return {
      src: `${API_URL}/images/${img}`,
      title: pick(meta.text1),
      text: pick(meta.text2),
      link: (meta.carouselLink && settings?.[meta.carouselLink]) || "",
    };
  }).filter(Boolean);

  const n = slides.length;
  const maxIndex = Math.max(0, n - perView);
  const clampedIndex = Math.min(index, maxIndex);

  useEffect(() => {
    const update = () => setPerView(window.innerWidth <= 768 ? 1 : 2);
    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
  }, []);

  useEffect(() => { if (index > maxIndex) setIndex(maxIndex); }, [maxIndex, index]);

  useEffect(() => {
    if (n <= perView || paused) return;
    const t = setTimeout(() => setIndex(i => (i >= maxIndex ? 0 : i + 1)), 5000);
    return () => clearTimeout(t);
  }, [clampedIndex, paused, n, perView, maxIndex]);

  if (n === 0) return null;

  const goToLink = (link) => () => {
    if (!link) return;
    if (/^https?:\/\//i.test(link)) window.open(link, "_blank", "noopener");
    else window.location.assign(link);
  };

  return (
    <div className="hero-cards" onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)}>
      <div className="hero-cards__viewport">
        <div className="hero-cards__track" style={{ transform: `translateX(-${clampedIndex * (100 / perView)}%)` }}>
          {slides.map((s, i) => (
            <div className="hero-card" key={i} aria-hidden={i < clampedIndex || i >= clampedIndex + perView}>
              <div
                className={`hero-card__inner ${s.link ? "is-link" : ""}`}
                onClick={s.link ? goToLink(s.link) : undefined}
                role={s.link ? "button" : undefined}
                tabIndex={s.link ? 0 : undefined}
                onKeyDown={s.link ? (e) => { if (e.key === "Enter") goToLink(s.link)(); } : undefined}
              >
                <img className="hero-card__img" src={s.src} alt="" loading={i < 2 ? "eager" : "lazy"} decoding="async" />
                <span className="hero-card__overlay" />
                <div className="hero-card__body">
                  {s.title && <div className="hero-card__title">{s.title}</div>}
                  {s.text && <div className="hero-card__text">{s.text}</div>}
                  {s.link && <span className="hero-card__cta">{common.more} <ArrowForwardIcon style={{ fontSize: 16 }} /></span>}
                </div>
              </div>
            </div>
          ))}
        </div>

        {maxIndex > 0 && (
          <>
            <button className="hero-cards__arrow hero-cards__arrow--prev" onClick={() => setIndex(i => Math.max(0, i - 1))} disabled={clampedIndex <= 0} aria-label="Previous">
              <ChevronLeftIcon />
            </button>
            <button className="hero-cards__arrow hero-cards__arrow--next" onClick={() => setIndex(i => Math.min(maxIndex, i + 1))} disabled={clampedIndex >= maxIndex} aria-label="Next">
              <ChevronRightIcon />
            </button>
          </>
        )}
      </div>

      {maxIndex > 0 && (
        <div className="hero-cards__dots">
          {Array.from({ length: maxIndex + 1 }).map((_, i) => (
            <button key={i} className={`hero-cards__dot ${i === clampedIndex ? "is-active" : ""}`} onClick={() => setIndex(i)} aria-label={`Go to slide ${i + 1}`} />
          ))}
        </div>
      )}
    </div>
  );
}

export default inject('feathersStore')(observer(Carousel));
